import React from 'react';

const VIZ_MODES = [
  { key: 'standard', label: 'Standard Clusters', icon: 'fas fa-braille', accent: '#047857', desc: 'Pie-chart cluster markers' },
  { key: 'individual', label: 'Individual Dots', icon: 'fas fa-circle', accent: '#f59e0b', desc: 'Individual unclustered dots' },
  { key: 'heatmap', label: 'Density Heatmap', icon: 'fas fa-fire', accent: '#ef4444', desc: 'Heat density overlay' },
  { key: 'fractal', label: 'Fractal Clusters', icon: 'fas fa-sitemap', accent: '#FDE68A', desc: 'Recursive depth clustering' },
  { key: 'voronoi', label: 'Voronoi Regions', icon: 'fas fa-draw-polygon', accent: '#10b981', desc: 'Tessellated territory map' },
];

const DATASET_MODES = [
  { key: 'category', label: 'Category', icon: 'fas fa-tags', accent: '#047857' },
  { key: 'doctype', label: 'Doc Type', icon: 'fas fa-file-alt', accent: '#f59e0b' },
  { key: 'quality', label: 'Quality', icon: 'fas fa-star', accent: '#10b981' }
];

const RIGHT_PANE_MODES = [
  { key: 'standard', label: 'Standard', icon: 'fas fa-braille', accent: '#047857' },
  { key: 'individual', label: 'Individual', icon: 'fas fa-circle', accent: '#f59e0b' },
  { key: 'heatmap', label: 'Heatmap', icon: 'fas fa-fire', accent: '#ef4444' },
  { key: 'fractal', label: 'Fractal', icon: 'fas fa-sitemap', accent: '#FDE68A' },
  { key: 'voronoi', label: 'Voronoi', icon: 'fas fa-draw-polygon', accent: '#10b981' },
];

const MapVizControls = ({
  vizModeAdvanced,
  setVizModeAdvanced,
  activeDatasets,
  toggleDataset,
  dualMapView,
  setDualMapView,
  mapUrlLimit,
  setMapUrlLimit,
  windowOverlay,
  setWindowOverlay,
  comparisonVizRight,
  setComparisonVizRight,
  dotColorMode,
  setDotColorMode
}) => {
  const showColorToggle = vizModeAdvanced === 'individual' || (dualMapView && comparisonVizRight === 'individual');
  return (
    <>
      {/* Advanced Visualization Mode — Radio Buttons */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 15, background: '#334155',
        borderRadius: 12, border: '1px solid rgba(252, 211, 77,0.25)', overflow: 'hidden',
        flexWrap: 'wrap'
      }} data-testid="advanced-viz-mode-panel">
        <div style={{
          padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 6,
          borderRight: '1px solid rgba(252, 211, 77,0.15)',
          background: 'rgba(252, 211, 77,0.08)'
        }}>
          <i className="fas fa-layer-group" style={{ color: '#047857', fontSize: '0.75rem' }} />
          <span style={{ color: '#047857', fontSize: '0.78rem', fontWeight: 700, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
            Visualization
          </span>
        </div>
        {VIZ_MODES.map((mode) => {
          const isActive = vizModeAdvanced === mode.key;
          return (
            <label
              key={mode.key}
              data-testid={`viz-radio-${mode.key}`}
              title={mode.desc}
              style={{
                padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                background: isActive ? `${mode.accent}18` : 'transparent',
                color: isActive ? '#e2e8f0' : '#71717a',
                fontSize: '0.78rem', fontWeight: isActive ? 700 : 500,
                borderRight: '1px solid rgba(252, 211, 77,0.1)',
                transition: 'all 0.25s', userSelect: 'none',
                borderBottom: isActive ? `2px solid ${mode.accent}` : '2px solid transparent',
              }}
            >
              <input
                type="radio"
                name="advancedVizMode"
                value={mode.key}
                checked={isActive}
                onChange={() => setVizModeAdvanced(mode.key)}
                style={{ accentColor: mode.accent, width: 14, height: 14, cursor: 'pointer' }}
              />
              <i className={mode.icon} style={{ fontSize: '0.72rem', color: isActive ? mode.accent : '#71717a' }} />
              <span>{mode.label}</span>
            </label>
          );
        })}
      </div>

      {/* Visualization Mode Checkboxes + Dual Map Toggle + URL Limit */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'stretch', marginBottom: 15, flexWrap: 'wrap'
      }}>
        {/* Dataset Checkboxes */}
        <div data-testid="viz-mode-toggle" style={{
          display: 'flex', gap: 0, background: '#334155', borderRadius: 10,
          border: '1px solid rgba(252, 211, 77,0.25)', overflow: 'hidden'
        }}>
          {DATASET_MODES.map(mode => (
            <label
              key={mode.key}
              data-testid={`viz-mode-${mode.key}`}
              style={{
                padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                background: activeDatasets[mode.key]
                  ? `${mode.accent}20`
                  : 'transparent',
                color: activeDatasets[mode.key] ? '#e2e8f0' : '#71717a',
                fontSize: '0.78rem', fontWeight: activeDatasets[mode.key] ? 700 : 500,
                borderRight: '1px solid rgba(252, 211, 77,0.15)',
                transition: 'all 0.2s', userSelect: 'none'
              }}
            >
              <input
                type="checkbox"
                checked={activeDatasets[mode.key]}
                onChange={() => toggleDataset(mode.key)}
                style={{ accentColor: mode.accent, width: 14, height: 14, cursor: 'pointer' }}
              />
              <i className={mode.icon} style={{ fontSize: '0.7rem', color: activeDatasets[mode.key] ? mode.accent : '#71717a' }} />
              {mode.label}
            </label>
          ))}
        </div>

        {/* Dual Map Toggle */}
        <div data-testid="dual-map-toggle" style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
          background: '#475569', borderRadius: 10,
          border: `1px solid ${dualMapView ? 'rgba(253, 230, 138,0.4)' : 'rgba(252, 211, 77,0.25)'}`
        }}>
          <span style={{ color: '#71717a', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
            <i className="fas fa-columns" style={{ marginRight: 4 }} />Compare
          </span>
          <div
            onClick={() => setDualMapView(prev => !prev)}
            style={{
              width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
              background: dualMapView ? 'linear-gradient(135deg, #FDE68A, #FCD34D)' : 'rgba(255,255,255,0.12)',
              position: 'relative', transition: 'background 0.3s'
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: '50%', background: 'white',
              position: 'absolute', top: 2, transition: 'left 0.3s',
              left: dualMapView ? 18 : 2, boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} />
          </div>
        </div>

        {/* URL Display Limit */}
        <div data-testid="map-url-limit" style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
          background: '#475569', borderRadius: 10,
          border: '1px solid rgba(245, 158, 11,0.25)'
        }}>
          <span style={{ color: '#71717a', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
            <i className="fas fa-sliders-h" style={{ marginRight: 4 }} />URLs:
          </span>
          <input
            data-testid="map-url-limit-input"
            type="range" min="50" max="2000" step="50"
            value={mapUrlLimit}
            onChange={(e) => setMapUrlLimit(parseInt(e.target.value))}
            style={{ width: 80, accentColor: '#3b82f6' }}
          />
          <span style={{ color: '#3b82f6', fontSize: '0.78rem', fontWeight: 700, minWidth: 35 }}>
            {mapUrlLimit}
          </span>
        </div>

        {/* Window Overlay indicator */}
        {windowOverlay && (
          <div data-testid="window-overlay-indicator" style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
            background: 'rgba(244,114,182,0.15)', borderRadius: 10,
            border: '1px solid rgba(244,114,182,0.4)', animation: 'popupFadeIn 0.3s ease-out'
          }}>
            <i className="fas fa-filter" style={{ color: '#047857' }} />
            <span style={{ color: '#047857', fontSize: '0.78rem', fontWeight: 600 }}>
              Showing: {windowOverlay.title || 'Window'} ({windowOverlay.results.length} URLs)
            </span>
            <button
              data-testid="clear-window-overlay"
              onClick={() => setWindowOverlay(null)}
              style={{
                background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)',
                color: '#ef4444', padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                fontSize: '0.7rem', fontWeight: 700
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Comparison mode: Right pane viz selector */}
      {dualMapView && (
        <div style={{
          display: 'flex', gap: 0, marginBottom: 12, background: 'rgba(16,185,129,0.04)',
          borderRadius: 10, border: '1px solid rgba(16,185,129,0.2)', overflow: 'hidden',
          flexWrap: 'wrap'
        }} data-testid="map-comparison-right-panel">
          <div style={{
            padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6,
            borderRight: '1px solid rgba(16,185,129,0.15)', background: 'rgba(16,185,129,0.08)'
          }}>
            <i className="fas fa-th-large" style={{ color: '#10b981', fontSize: '0.68rem' }} />
            <span style={{ color: '#10b981', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Right Pane</span>
          </div>
          {RIGHT_PANE_MODES.map((mode) => {
            const isActive = comparisonVizRight === mode.key;
            return (
              <label key={mode.key} data-testid={`map-viz-right-${mode.key}`}
                style={{
                  padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  background: isActive ? `${mode.accent}18` : 'transparent',
                  color: isActive ? '#e2e8f0' : '#71717a',
                  fontSize: '0.72rem', fontWeight: isActive ? 700 : 500,
                  borderRight: '1px solid rgba(16,185,129,0.1)',
                  transition: 'all 0.25s', userSelect: 'none',
                  borderBottom: isActive ? `2px solid ${mode.accent}` : '2px solid transparent',
                }}>
                <input type="radio" name="mapVizRight" value={mode.key}
                  checked={isActive} onChange={() => setComparisonVizRight(mode.key)}
                  style={{ accentColor: mode.accent, width: 12, height: 12, cursor: 'pointer' }} />
                <i className={mode.icon} style={{ fontSize: '0.65rem', color: isActive ? mode.accent : '#71717a' }} />
                <span>{mode.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* Dot Color Toggle — shown when Individual mode is active */}
      {showColorToggle && setDotColorMode && (
        <div style={{
          display: 'flex', gap: 0, marginBottom: 12, background: 'rgba(245,158,11,0.04)',
          borderRadius: 10, border: '1px solid rgba(245,158,11,0.25)', overflow: 'hidden',
        }} data-testid="map-dot-color-toggle-bar">
          <div style={{
            padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6,
            borderRight: '1px solid rgba(245,158,11,0.15)', background: 'rgba(245,158,11,0.08)'
          }}>
            <i className="fas fa-palette" style={{ color: '#f59e0b', fontSize: '0.75rem' }} />
            <span style={{ color: '#f59e0b', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Dot Color By
            </span>
          </div>
          {[
            { key: 'category', label: 'Category / Subcategory', icon: 'fas fa-folder', accent: '#047857' },
            { key: 'doctype', label: 'Document Type', icon: 'fas fa-file-alt', accent: '#3b82f6' },
          ].map((opt) => {
            const isActive = dotColorMode === opt.key;
            return (
              <label key={opt.key} data-testid={`map-dot-color-${opt.key}`}
                style={{
                  padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  background: isActive ? `${opt.accent}18` : 'transparent',
                  color: isActive ? '#e2e8f0' : '#71717a',
                  fontSize: '0.78rem', fontWeight: isActive ? 700 : 500,
                  borderRight: '1px solid rgba(245,158,11,0.1)',
                  transition: 'all 0.25s', userSelect: 'none',
                  borderBottom: isActive ? `2px solid ${opt.accent}` : '2px solid transparent',
                }}>
                <input type="radio" name="mapDotColorMode" value={opt.key}
                  checked={isActive} onChange={() => setDotColorMode(opt.key)}
                  style={{ accentColor: opt.accent, width: 14, height: 14, cursor: 'pointer' }} />
                <i className={opt.icon} style={{ fontSize: '0.72rem', color: isActive ? opt.accent : '#71717a' }} />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </>
  );
};

export default MapVizControls;
